import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * SiteSettingsContext — loads /api/site-settings/public once at app start
 * and exposes business info, homepage config, page visibility, and navigation
 * to every public component. Falls back to sensible defaults if the API is
 * unreachable so the site never renders blank.
 */

const API = process.env.REACT_APP_BACKEND_URL;

const FALLBACK = {
  business: {
    name: 'TayLord Arms LLC',
    tagline: 'Expert Craftsmanship, Reliable Service',
    phone: '(843) 209-4304',
    email: 'info.taylordarms@gmail.com',
    address_line1: '1102 Maplecrest Dr',
    address_line2: 'Moncks Corner, SC 29461',
    hours_weekday: 'Mon-Fri: 10:00 AM - 5:00 PM',
    hours_saturday: 'Sat: 10:00 AM - 2:00 PM',
    hours_sunday: 'Sunday: Closed',
    google_maps_url: null,
    facebook_url: null,
    instagram_url: null,
  },
  homepage: {
    headline: 'Expert Craftsmanship, Reliable Service',
    subline: 'Professional gunsmithing services for the responsible firearm owner.',
    primary_cta_text: 'Request a Quote',
    primary_cta_link: '/#contact',
    secondary_cta_text: 'View Our Work',
    secondary_cta_link: '/gallery',
    hero_image_url: '/logo-taylordarms.png',
    sections: [
      { key: 'hero', label: 'Hero', visible: true, order: 1 },
      { key: 'services', label: 'Services', visible: true, order: 2 },
      { key: 'gallery_preview', label: 'Gallery', visible: true, order: 3 },
      { key: 'values', label: 'Values', visible: true, order: 4 },
      { key: 'contact', label: 'Contact', visible: true, order: 5 },
    ],
  },
  pages: [],
  navigation: [
    { key: 'home',     label: 'Home',          path: '/',              visible: true, order: 1 },
    { key: 'services', label: 'Services',      path: '/#services',     visible: true, order: 2 },
    { key: 'gallery',  label: 'Gallery',       path: '/gallery',       visible: true, order: 3 },
    { key: 'ffl',      label: 'FFL Transfers', path: '/ffl-transfers', visible: true, order: 4 },
    { key: 'about',    label: 'About',         path: '/#about',        visible: true, order: 5 },
    { key: 'contact',  label: 'Contact',       path: '/#contact',      visible: true, order: 6 },
  ],
  maintenance: { enabled: false },
};

const SiteSettingsContext = createContext(FALLBACK);

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(FALLBACK);
  const [loaded, setLoaded] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/api/site-settings/public`);
      setSettings({
        ...FALLBACK,
        ...data,
        business: { ...FALLBACK.business, ...(data.business || {}) },
        homepage: { ...FALLBACK.homepage, ...(data.homepage || {}) },
        pages: data.pages || [],
        navigation: (data.navigation || FALLBACK.navigation).slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
      });
    } catch {
      // keep fallback
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <SiteSettingsContext.Provider value={{ ...settings, loaded, refresh }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => useContext(SiteSettingsContext);

/**
 * Helper to look up a page's status by key.
 * Returns 'published' if the page isn't tracked.
 */
export const usePageStatus = (pageKey) => {
  const { pages } = useSiteSettings();
  const p = (pages || []).find((x) => x.key === pageKey);
  return p || { key: pageKey, status: 'published' };
};
